import { FileOnSocietyType, S3File } from "@dune/types";
import { Card, DataTable } from "@dune/ui";
import { FileWarning } from "lucide-react";
import { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { toast } from "sonner";
import { useUploadBrokerDocument } from "../../../../api/mutations/societies.mutations";
import { S3ButtonInput } from "../../../../component/common/S3ButtonInput";
import { useDocumentStore } from "../../../../stores/document.store";
import { FilesAnalyze, filesColumns } from "../filesColumns";
import { PartnershipRequestAnalysisFormData } from "../usePartnershipRequestAnalysis";

interface DocumentSectionProps {
	title: string;
	documents: FilesAnalyze[];
	documentType: FileOnSocietyType;
	fieldName: keyof Pick<
		PartnershipRequestAnalysisFormData,
		| "companyDocuments"
		| "userDocuments"
		| "lcbftDocuments"
		| "rcDocuments"
		| "bankDocuments"
	>;
	getValues: UseFormGetValues<PartnershipRequestAnalysisFormData>;
	setValue: UseFormSetValue<PartnershipRequestAnalysisFormData>;
	societyId: number;
}

export const DocumentSection = ({
	title,
	documents,
	documentType,
	fieldName,
	getValues,
	setValue,
	societyId,
}: DocumentSectionProps) => {
	const { setFileId } = useDocumentStore();
	const { mutateAsync: uploadDocument } = useUploadBrokerDocument();

	const handleUploadSuccess = async (s3File: S3File) => {
		try {
			const uploadedDocument = await uploadDocument({
				brokerId: societyId,
				name: s3File.name,
				key: s3File.key,
				bucket: s3File.bucket,
				mimeType: s3File.type,
				type: documentType,
			});

			const currentDocs = getValues(fieldName) || [];
			const newDocument = {
				type: documentType,
				file: {
					id: uploadedDocument.file.id,
					name: uploadedDocument.file.name,
					key: uploadedDocument.file.key,
					mimeType: uploadedDocument.file.mimeType,
					bucket: uploadedDocument.file.bucket,
					humanProcessingStatus: uploadedDocument.file.humanProcessingStatus,
				},
				createdAt: uploadedDocument.createdAt,
				updatedAt: uploadedDocument.updatedAt,
			};

			setValue(fieldName, [...currentDocs, newDocument], {
				shouldDirty: true,
				shouldValidate: true,
			});

			toast.success("Document uploadé avec succès");
		} catch (error) {
			console.error("Error uploading document:", error);
			toast.error("Erreur lors de l'upload du document");
		}
	};

	const handleRowClick = (row: FilesAnalyze) => {
		setFileId(row.file.id);
	};

	return (
		<Card className="p-6 flex flex-col gap-4 overflow-auto text-center">
			<div className="flex flex-row justify-between items-center">
				<div className="text-text-secondary-blue">{title}</div>
				<S3ButtonInput bucket="private" onUploadSuccess={handleUploadSuccess} />
			</div>

			<DataTable
				columns={filesColumns({ loadingFileKey: null })}
				data={documents}
				noResultsText="Aucun fichier disponible"
				icon={FileWarning}
				variant="static-clean"
				onRowClick={handleRowClick}
			/>
		</Card>
	);
};
